import React, { useState, useEffect, useCallback, useRef, CSSProperties } from 'react';
import { Navigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';

import { Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { 
  DandDFeatureSource,
  SourceBook,
  Spell,
} from "@dndModels";
import { DisablableOption, FloatingFeature, ModelBase } from "@models";
import { 
  DATA_TYPE_INFO,
  SOURCE_BOOK_TYPES, 
  WORLD_SETTINGS
} from "@dndModels/Constants";
import { SourceBookType, WorldSetting } from "@dndModels/Types";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";
// import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
// import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import LoadingBox from "@components/input/LoadingBox";

import CheckBox from "@components/input/CheckBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ImageButton from "@dndComponents/other/ImageButton";
import DescriptionBox from "@dndComponents/DescriptionBox";
import SelectSourceBookBox from "@dndComponents/select/SelectSourceBookBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import { isModelBaseWithEffects, ModelBaseWithEffects } from '@utilities/smart_api_class';

interface Props { 
  obj: ModelBase | DandDFeatureSource;
  onChange: (changed: ModelBase | DandDFeatureSource) => void;
  no_grid?: boolean;
  no_source?: boolean;
  disabled?: boolean;
  change_time?: number;
  image_options?: string[];
  // get_creature_homebrewery_string?: (ci: BasicCreatureInstance | undefined) => string;
  // get_homebrewery_string?: () => string;
  children?: React.ReactNode;
  submitControls?: React.ReactNode;
  onSubmit?: (changed: ModelBase | DandDFeatureSource) => void;
  onSubmitted?: (changed: ModelBase | DandDFeatureSource) => void;
  onCancel?: () => void;
  processing?: boolean;
  topHeight?: number;
  no_scrollbar?: boolean;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
// const char_util = DandDCharacterUtilities.getInstance();

export default function ModelBaseDetails({
  obj,
  onChange,
  change_time = 0,
  no_grid = false,
  disabled = false,
  no_source = false,
  image_options,
  // get_creature_homebrewery_string,
  // get_homebrewery_string,
  children,
  submitControls,
  onSubmit,
  onSubmitted,
  onCancel,
  processing: inherited_processing,
  topHeight = 0,
  no_scrollbar
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [myObj, setObj] = useState<ModelBase | DandDFeatureSource>(obj.clone());
  const [version_options, setVersionOptions] = useState<DisablableOption[]>([]);
  const [basicObj, setBasicObj] = useState<ModelBase | undefined>(undefined);
  const [description, setDescription] = useState(obj.description);
  const [notes, setNotes] = useState(obj.notes);
  const [sourceTypes, setSourceTypes] = useState<SourceBookType[]>([]);
  const [my_change_time, setChangeTime] = useState(change_time);
  // const [homebrewery_cr, setHomebreweryCR] = useState(-1);
  const [name_for_helmet, setNameForHelmet] = useState(obj.get_use_name());
  // const [is_dominant, setIsDominant] = useState(obj.is_true_dominant);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [submitHeight, setSubmitHeight] = useState(0);
  const [submit_height_changed, setSubmitHeightChanged] = useState(false);

  const submitRef = useCallback((el: HTMLDivElement | null) => {
    if (el) {
      // debugger;
      setSubmitHeight(el.getBoundingClientRect().height);
      setSubmitHeightChanged(true);
    }
  }, [disabled, submitControls]); // deps that change submit height

  useEffect(() => {
    if (submit_height_changed) {
      setSubmitHeightChanged(false);
    }
  }, [submit_height_changed, setSubmitHeightChanged]);

  const load = useCallback(async () => {
    // debugger;
    console.log("ModelBaseDetails load()");
    if (obj._id !== "" && !obj.loaded) {
      await api.loadObject(obj);
      setObj(obj);
      load();
    } else {
      setNameForHelmet(myObj.get_use_name());
      if (myObj.source_book_types.length === 0) {
        if (api.user && api.user.admin) {
          setSourceTypes(["Source Book"]);
        } else {
          setSourceTypes(["Homebrew"]);
          const new_obj = myObj;
          new_obj.source_books_old.push({ type: "Homebrew", id: "Personal", third_party: false });
          setObj(new_obj);
        }
      } else {
        setSourceTypes(myObj.source_book_types);
      }
      // debugger;
      let new_objects = await api.getObjects({ data_type: myObj.data_type }) as ModelBase[];
      
      if (!myObj.source_book_ids.includes("63cd69a14d337b1738dab548")) {
        const basic_finder = new_objects.filter(o => o.name === myObj.name && o._id !== myObj._id);
        if (basic_finder.length > 0) {
          if (basic_finder.length === 1) {
            setBasicObj(basic_finder[0]);
          } else {
            const new_basic = basic_finder.find(o => o.source_book_ids.includes("63cd69a14d337b1738dab548"));
            if (new_basic) {
              setBasicObj(new_basic);
            } 
          }
        }
      }
      new_objects = new_objects.filter(o => o._id === o.variant_group_id && o._id !== myObj._id);
      setVersionOptions(new_objects.map(o => {
        const opt: DisablableOption = {
          id: o.variant_group_id, 
          text: o.name
        }
        return opt;
      }));
      setLoading(false);
    }
  }, [myObj, obj]);
  
  useEffect(() => {
    if (!fetched) {
      setLoading(true);
      setFetched(true);
      load();
    }
  }, [fetched, setFetched, setLoading, load]);

  useEffect(() => {
    if (change_time !== my_change_time) {
      const new_obj = obj._id !== myObj._id;

      setObj(obj.clone());
      setDescription(obj.description);
      setChangeTime(change_time);

      if (new_obj) {
        setFetched(false);
      }
    }
    // eslint-disable-next-line
  }, [change_time, my_change_time, obj]);

  const render_description = () => {
    if (disabled) {
      if (description.length > 0) {
        return (
          <Grid
            key="description"
            size={{
              xs: 12
            }}>
            <DisplayStringWithReplacements display_string={description} />
          </Grid>
        );
      } else {
        return null;
      }
    } else {
      return (
        <Grid
          key="description"
          size={{
            xs: 12
          }}>
          <DescriptionBox
            value={description}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = myObj;
              new_obj.description = changed;
              setObj(new_obj);
              setDescription(changed);
              setChangeTime(common_util.get_time_number());
              onChange(new_obj);
            }}
          />
        </Grid>
      );
    }
  }

  const render_notes = () => {
    if (disabled) {
      if (notes.length > 0) {
        return (
          <Grid
            key="notes"
            size={{
              xs: 12
            }}>
            <DisplayStringWithReplacements display_string={notes} />
          </Grid>
        );
      } else {
        return null;
      }
    } else {
      return (
        <Grid
          key="notes"
          size={{
            xs: 12
          }}>
          <DescriptionBox
            name="Notes"
            value={notes}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = myObj;
              new_obj.notes = changed;
              setObj(new_obj);
              setNotes(changed);
              setChangeTime(common_util.get_time_number());
              onChange(new_obj);
            }}
          />
        </Grid>
      );
    }
  }

  const render_source = () => {
    if (myObj instanceof SourceBook) {
      if (disabled) {
        return (
          <Grid key="source" container spacing={0} direction="row">
            <Grid
              size={{
                xs: 6
              }}>
              <span className="header">Type:</span> { myObj.source_book_type }
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              { myObj.is_free ? `This Source Book is free` : "" }
            </Grid>
          </Grid>
        );
      } else {
        return (
          <Grid key="source" container spacing={0} direction="row">
            <Grid
              size={{
                xs: 6
              }}>
              <SelectStringBox
                name="Type"
                options={SOURCE_BOOK_TYPES}
                value={ myObj.source_book_type } 
                disabled={ disabled || !api.user || !api.user.admin }
                onChange={(changed: SourceBookType) => {
                  const new_obj = myObj.clone();
                  new_obj.source_book_type = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  onChange(new_obj);
                }}
              />
            </Grid>
            { !disabled &&
              <Grid
                size={{
                  xs: 6
                }}>
                <ToggleButtonBox
                  name="Free"
                  value={ myObj.is_free } 
                  disabled={disabled}
                  onToggle={() => {
                    const new_obj = myObj.clone();
                    new_obj.is_free = !new_obj.is_free;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    onChange(new_obj);
                  }}
                />
              </Grid>
            }
            { api.user && api.user.admin &&
              <Grid
                size={{
                  xs: 6
                }}>
                <ToggleButtonBox
                  name="Public"
                  value={ myObj.is_public } 
                  disabled={disabled}
                  onToggle={() => {
                    const new_obj = myObj.clone();
                    new_obj.is_public = !new_obj.is_public;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    onChange(new_obj);
                  }}
                />
              </Grid>
            }
          </Grid>
        );
      }
    } else {
      if (disabled) {
        return (
          <span key="source">In <DisplayObjects type="source_book" ids={myObj.source_book_ids} /></span>
        );
      } else if (api.user) {
        if (api.user.admin) {
          return (
            <SelectSourceBookBox key="source"
              types={sourceTypes}
              ids={myObj.source_book_ids}
              multiple
              onChangeMultiple={(types, ids) => {
                setSourceTypes(types);
                const new_obj = myObj;
                const source_books = api.source_books.filter(o => ids.includes(o._id));
                if (new_obj) {
                  // Selected books that aren't in api.source_books (e.g. blocked ones while
                  // view_blocked_source_books is off) are kept as they were rather than dropped.
                  const unlisted = new_obj.source_books_old.filter(o =>
                    ids.includes(o.id) && !source_books.some(sb => sb._id === o.id));
                  new_obj.source_books_old = [
                    ...source_books.map(o => { return { type: o.source_book_type, id: o._id, third_party: o.third_party }; }),
                    ...unlisted
                  ];
                  new_obj.source_books = source_books;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  onChange(new_obj);
                }
              }}
            />
          );
        } else {
          return (
            <Grid key="source" container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 12
                }}>
                <SelectObjectBox
                  name="Source Book"
                  value={ myObj.source_book_ids[0] } 
                  data_type="source_book"
                  filter={{ owner_id: api.user._id }}
                  extra_options={["Personal"]}
                  disabled={disabled}
                  onChange={(changed: string) => {
                    const new_obj = myObj;
                    if (changed === "Personal") {
                      new_obj.source_books_old = [{ type: "Homebrew", id: "Personal", third_party: false }];
                    } else {
                      const source_books = api.source_books.filter(o => changed === o._id);
                      if (new_obj) {
                        new_obj.source_books_old = source_books.map(o => { return { type: o.source_book_type, id: o._id, third_party: o.third_party }; });
                      }
                    }
                    new_obj.source_books = api.source_books.filter(o => new_obj.source_book_ids.includes(o._id));
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    onChange(new_obj);
                  }}
                />
              </Grid>
            </Grid>
          );
        }
      }
      return null;
    }
  }

  const render_version = () => {
    if (!disabled) {
      return (
        <Grid key="version" container spacing={0} direction="row">
          <Grid size={{ xs: myObj.variant_of_id === "" ? 12 : 6 }}>
            <SelectStringBox
              name="Version of"
              options={version_options}
              extra_options={[{id: "", text: "None"}]}
              value={ myObj.variant_of_id }
              disabled={disabled}
              onChange={(id: string) => {
                const new_obj = myObj;
                // new_obj.dominance_level = 0;
                const changed = api.getDataTypeFromHash(myObj.data_type).find(o => o._id === id);
                if (changed instanceof ModelBase) {
                  new_obj.variant_of_id = changed.variant_group_id;
                  new_obj.variant_ids = [...changed.variant_ids];
                  new_obj.variant_group = [...changed.variant_group];
                } else {
                  new_obj.variant_of_id = "";
                  new_obj.variant_ids = [];
                  new_obj.variant_group = [];
                  new_obj.true_variant = false;
                }
                // setDominance(new_obj, false);
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
                onChange(new_obj);
              }}
            />
          </Grid>
          { myObj.variant_of_id !== "" ?
            <>
              <Grid size={4}>
                <ButtonBox
                  name="None"
                  disabled={disabled}
                  onClick={() => {
                    const new_obj = myObj;
                    new_obj.variant_of_id = "";
                    new_obj.variant_ids = [];
                    new_obj.variant_group = [];
                    new_obj.true_variant = false;
                    // setDominance(new_obj, false);
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number()); 
                    onChange(new_obj);
                  }}
                />
              </Grid>
              <Grid size={2}>
                <div className="padded">
                  <CheckBox
                    name="Non-Version Variant"
                    value={myObj.true_variant}
                    disabled={disabled}
                    onChange={(changed: boolean) => {
                      const new_obj = myObj;
                      new_obj.true_variant = changed;
                      // setDominance(new_obj, false);
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number()); 
                      onChange(new_obj);
                    }}
                  />
                </div>
              </Grid>
              {/* <Grid
                size={{
                  xs: 2
                }}>
                <CheckBox
                  name="Use Base Name"
                  value={myObj.use_base_name}
                  disabled={disabled || myObj.true_variant}
                  onChange={(changed: boolean) => {
                    const new_obj = myObj;
                    new_obj.use_base_name = changed;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    onChange(new_obj);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 2
                }}>
                <CheckBox
                  name="Dominant Version"
                  value={is_dominant}
                  disabled={disabled || myObj.true_variant}
                  onChange={(changed: boolean) => {
                    const new_obj = myObj;
                    setDominance(new_obj, changed);
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number()); 
                    onChange(new_obj);
                  }}
                />
              </Grid> */}
            </>
          :
            renderBasicWithName()
          }
        </Grid>
      );
    }
    return null;
  }

  const renderBasicWithName = () => {
    if (basicObj) {
      return (
        <Grid size={12}>
          <ButtonBox
            name={`Use one from ${basicObj.source_book_abbrev}`}
            disabled={disabled}
            onClick={() => {
              const new_obj = myObj;
              // new_obj.dominance_level = 0;
              if (basicObj instanceof ModelBase) {
                new_obj.variant_of_id = basicObj._id;
                new_obj.variant_ids = [...basicObj.variant_ids];
                new_obj.variant_group = [...basicObj.variant_group];
              }
              // setDominance(new_obj, false);
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
              onChange(new_obj);
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  const render_settings = () => {
    if (disabled) {
      if (myObj.world_settings.length > 0) {
        return (
          <span key="world_settings">{ myObj.world_settings.join(", ") }</span>
        );
      }
    } else {
      return (
        <SelectStringBox key="world_settings"
          name="World Settings"
          options={WORLD_SETTINGS}
          values={myObj.world_settings}
          auto_sort
          onChange={(changed: WorldSetting[]) => {
            const new_obj = myObj;
            new_obj.world_settings = changed;
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
            onChange(new_obj);
          }}
        />
      );
    }
    return null;
  }

  const render_image = () => {
    if (!disabled) {
      return (
        <ImageButton key="image"
          name='Image'
          image_url={obj.image_url}
          select_options={image_options}
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.image_url = changed;
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
            onChange(new_obj);
          }}
        />
      );
    }
    return null;
  }

  const homebrewery_export_btn = () => {
    if (disabled) {
      return null;
    } else {
      return (
        <Grid container spacing={0} direction="row" key="homebrewery_export_btn">
          <Grid
            size={{
              xs: 12
            }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setOpen(true);
              }}>
              Homebrewery Export
            </Button>
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <ModalOrDrawer
              open={open}
              onClose={() => {
                setOpen(false);
              }}>
              <Grid container spacing={0} direction="row">
                {/* { open && homebrewery_export() } */}
              </Grid>
            </ModalOrDrawer>
          </Grid>
        </Grid>
      );
    }
  }

  const submit = () => {
    // debugger;
    if (onSubmit) {
      onSubmit(myObj);
    } else {
      standardSubmit();
    }
  }

  const standardSubmit = async () => {
    if (api.user && 
      (api.user.admin || 
        obj._id === "" || 
        obj.owner_id === api.user._id)) {
      setProcessing(true);
      if (!api.user.admin || obj.source_books_old.filter(o => o.id === "Personal").length > 0) {
        obj.owner_id = api.user._id;
      } else if (api.user.admin && obj.source_books_old.filter(o => o.type === "Homebrew").length === 0) {
        obj.owner_id = "";
      }
      // if (api.user.admin && (!api.working_on_sb || obj.source_book_ids[0] !== api.working_on_sb._id)) {
      //   debugger; // TRYING TO FIGURE OUT WHY THINGS ARE GOING TO 5.1
      // }
      const res = await api.upsertObject(obj.data_type, obj);
      // debugger;
      if (res && res.res instanceof ModelBase) {
        const changed = res.res;
        api.handleVersion(changed, changed.data_type);
        changed.loaded = true;
        // debugger;
        changed.source_books = api.source_books.filter(o => changed.source_book_ids.includes(o._id));
        const source_books = changed.source_books;
        const ids = changed.source_book_ids;
        const handleModelBaseWithEffects = async (mb: ModelBaseWithEffects) => {
          const summon_effects = mb.effects.filter(o => o.type === "Summon");
          const transform_effects = mb.effects.filter(o => o.type === "Transform");
          for (const effect of summon_effects) {
            for (const potence of effect.potences) {
              for (const opt of potence.summon_options) {
                if (opt.summon_type === "Custom" && opt.custom_stat_block) {
                  const stat_block = opt.custom_stat_block;
                  for (const fb of stat_block.fbs) {
                    for (const f of fb.features) {
                      if (f.floating_feature) {
                        await handleFF(f.floating_feature);
                      }
                    }
                  }
                }
              }
            }
          }
          for (const effect of transform_effects) {
            for (const potence of effect.potences) {
              for (const opt of potence.transform_options) {
                for (const fb of opt.fbs) {
                  for (const f of fb.features) {
                    if (f.floating_feature) {
                      await handleFF(f.floating_feature);
                    }
                  }
                }
              }
            }
          }
        }
        const handleFF = async (ff: FloatingFeature) => {
          if (ff) {
            if (!common_util.list1ContainsList2(ff.source_book_ids, ids)) {
              const new_obj = ff;
              for (const sb of source_books) {
                if (!new_obj.source_book_ids.includes(sb._id)) {
                  new_obj.source_books_old.push({ type: sb.source_book_type, id: sb._id, third_party: sb.third_party });
                  new_obj.source_books.push(sb);
                }
              }
              await api.upsertObject(new_obj.data_type, new_obj);
            }
            if (isModelBaseWithEffects(ff)) {
              await handleModelBaseWithEffects(ff);
            }
          }
        }
        if (changed instanceof DandDFeatureSource) {
          // Go through all the features on it, and make sure they're all associated with the source books that this is.
          for (const fb of changed.feature_bases) {
            for (const f of fb.features) {
              if (f.floating_feature) {
                await handleFF(f.floating_feature);
              }
            }
          }
          if (onSubmitted) {
            onSubmitted(changed);
          }
        } else if (isModelBaseWithEffects(changed)) {
          await handleModelBaseWithEffects(changed);
        }
        if (obj instanceof DandDFeatureSource) {
          if (changed instanceof DandDFeatureSource) {
            obj.copy(changed);
          }
        } else {
          obj.copy(changed);
        }
        if (onSubmitted) {
          onSubmitted(changed);
        }
      }
      setProcessing(false);
      setNavigateTo(`/${obj.data_type}`);
    }
  }

  const cancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setNavigateTo(`/${obj.data_type}`);
    }
  }

  const renderSubmit = () => {
    // debugger;
    if (disabled && !submitControls) {
      return null;
    }
    
    const renderIt = () => {
      if (submitControls) {
        return submitControls;
      } else {
        return (
          <>
            <Button
              variant="contained"
              color="primary"
              disabled={processing || inherited_processing}
              onClick={submit}>
              Submit
            </Button>
            <Button
              variant="contained"
              disabled={processing || inherited_processing}
              style={{ marginLeft: "4px" }}
              onClick={cancel}>
              Cancel
            </Button>
          </>
        );
      }
    }
    return (
      <Grid
        key="submit"
        size={{
          xs: 12
        }} ref={submitRef}>
        { renderIt() }
      </Grid>
    );
  }

  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    // debugger;
    const return_us: any[] = [];
    if (no_grid) {
      return_us.push(render_description());
      return_us.push(render_notes());
      if (children) {
        return_us.push(<React.Fragment key="children">{children}</React.Fragment>);
      }
      return_us.push(homebrewery_export_btn());
      if (!no_source) {
        return_us.push(render_source());
      }
      return_us.push(render_version());
      return_us.push(render_settings());
      return_us.push(render_image());
      return_us.push(renderSubmit());
    } else {
      // console.log(topHeight);
      // console.log(submitHeight);
      const style: CSSProperties | undefined = no_scrollbar ? undefined : { maxHeight: `calc(100% - ${submitHeight + 10 /** for padded class */}px)`, overflowY: "auto" };
      return_us.push(
        <Grid container key="all" spacing={0} direction="row" style={{ height: "100%" }}>
          { !submit_height_changed && 
            <Grid size={12} 
              style={style}>
              <Grid container spacing={0} direction="row" style={{ height: "100%" }}>
                { render_description() }
                { render_notes() }
                { children && 
                  <Grid size={12}>
                    { children }
                  </Grid>
                }
                {/* <Grid
                  size={{
                    xs: 12
                  }}>
                  { homebrewery_export_btn() }
                </Grid> */}
                { !no_source &&
                  <Grid size={12}>
                    { render_source() }
                  </Grid>
                }
                <Grid size={12}>
                  { render_version() }
                </Grid>
                {/* <Grid
                  size={{
                    xs: 12
                  }}>
                  { render_settings() }
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { render_image() }
                </Grid> */}
              </Grid>
            </Grid>
          }
          { renderSubmit() }
        </Grid>
      );
    }
    const dt_info = DATA_TYPE_INFO.find(o => o.data_type === obj.data_type);
    // console.log(topHeight);
    // console.log(myObj);
    return (
      <>
        <Helmet>
          <title>Homebrew Game Labs - { myObj._id === "" ? `Create ${dt_info?.name??"Object"}` : `Edit ${dt_info?.name??"Object"}: ${name_for_helmet}` }</title>
        </Helmet>
        { return_us }
      </>
    );
  }
}